
import React, { useState, useRef, useEffect, useCallback } from 'react';
import AccordionCard from './AccordionCard';
import { HealthCategory, HealthRecord } from '../types';
import { healthCategories, adlActivities, ioCategories, emotions, gcsCategories, physiotherapyActivities, feedingTypes, mealCategories, unitCodes, stoolSubTypes, urineSubTypes, stoolColors, urineColors, outputAmounts, bristolStoolTypes, painLevels, vitalIcons, outputClassifications } from '../constants';
import { API_BASE_URL, API_KEY, API_DOMAIN } from '../user_constant';
import { encodeFoodItems } from '../foodEncoder';
import { buildImportantNoteInsert } from '../utils';
import { ImportantToggle, ImportantWarningAlert } from './ImportantUI';
import LexicalQuillCompat from '../../../components/RichText/LexicalQuillCompat';
import { iconSizeClass, iconToken, EXPANDED_PANEL_CLASS } from '../iconSizing';
import '../../../components/RichText/rte.css';

interface HealthInputPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onAddRecord: (record: HealthRecord) => void;
  employeeNumber: string;
  patientId: string;
}

// AssetIcon component for rendering image assets with fallback text
interface AssetIconProps {
  src?: string;
  label: string;
  className?: string;
  imgClassName?: string;
}

const AssetIcon: React.FC<AssetIconProps> = ({ src, label, className = '', imgClassName = '' }) => {
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    setHasError(true);
  };

  if (!src || hasError) {
    // Fallback: render label text in a styled span
    return (
      <span className={`flex items-center justify-center text-gray-700 font-medium text-xs ${className}`}>
        {label}
      </span>
    );
  }

  return (
    <img
      src={src}
      alt={label}
      loading="lazy"
      onError={handleError}
      className={`object-contain ${className} ${imgClassName}`}
    />
  );
};

const VITAL_VALIDATIONS: Record<string, { min?: number; max?: number; integer?: boolean }> = {
    'Weight (kg)': { min: 1, max: 300 },
    'Heart Rate (bpm)': { min: 30, max: 250, integer: true },
    'BP Systolic': { min: 50, max: 300, integer: true },
    'BP Diastolic': { min: 30, max: 200, integer: true },
    'Glucose (mmol/L)': { min: 1, max: 50 },
    'Sleep (hrs)': { min: 0, max: 24 },
    'Temp (°C)': { min: 34.0, max: 43.0 },
    'SpO2 (%)': { min: 70, max: 100, integer: true },
    'Respiration (bpm)': { min: 5, max: 60, integer: true },
};
const NOTE_CHAR_LIMIT = 500;

// Helper to convert blob URL to data URL for persistent storage
const blobUrlToDataUrl = (blobUrl: string): Promise<string> => {
    return fetch(blobUrl)
        .then(res => res.blob())
        .then(blob => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        }));
};

const HumanBodyDiagram = ({ selectedLocations, onLocationClick, view }: { selectedLocations: string[], onLocationClick: (name: string) => void, view: 'front' | 'back' }) => {
  const commonPartStyle = "cursor-pointer transition-all duration-200 ease-in-out";
  const selectedStyle = "fill-primary";
  const unselectedStyle = "fill-gray-300 hover:fill-primary-light";

  // FIX: Refactored to a render function to avoid TypeScript errors with the 'key' prop
  // and to prevent re-defining a component on every render, which is a performance anti-pattern.
  const renderBodyPart = (part: { name: string, d: string }) => {
    const isSelected = selectedLocations.includes(part.name);
    return (
      <path
        key={part.name}
        d={part.d}
        onClick={() => onLocationClick(part.name)}
        className={`${commonPartStyle} ${isSelected ? selectedStyle : unselectedStyle}`}
        stroke="#FFFFFF"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    );
  };
  
  const frontParts = [
    { name: 'Head', d: 'M140,40 C120,40 100,60 100,80 C100,110 120,130 140,130 C160,130 180,110 180,80 C180,60 160,40 140,40 Z' },
    { name: 'Right Neck', d: 'M150,130 L160,130 L170,145 L160,145 Z' },
    { name: 'Left Neck', d: 'M130,130 L120,130 L110,145 L120,145 Z' },
    { name: 'Chest', d: 'M120,145 L160,145 L170,200 L110,200 Z' },
    { name: 'Abdomen', d: 'M110,200 L170,200 L165,260 L115,260 Z' },
    { name: 'Right Shoulder', d: 'M170,145 L160,145 L180,155 L190,150 Z' },
    { name: 'Left Shoulder', d: 'M110,145 L120,145 L100,155 L90,150 Z' },
    { name: 'Right Upper Arm', d: 'M190,150 L180,155 L190,210 L200,205 Z' },
    { name: 'Left Upper Arm', d: 'M90,150 L100,155 L90,210 L80,205 Z' },
    { name: 'Right Elbow', d: 'M200,205 L190,210 L195,225 L205,220 Z' },
    { name: 'Left Elbow', d: 'M80,205 L90,210 L85,225 L75,220 Z' },
    { name: 'Right Lower Arm', d: 'M205,220 L195,225 L200,280 L210,275 Z' },
    { name: 'Left Lower Arm', d: 'M75,220 L85,225 L80,280 L70,275 Z' },
    { name: 'Right Hand', d: 'M210,275 L200,280 L205,310 L215,305 Z' },
    { name: 'Left Hand', d: 'M70,275 L80,280 L75,310 L65,305 Z' },
    { name: 'Private', d: 'M135,260 L145,260 L145,280 L135,280 Z' },
    { name: 'Right Groin', d: 'M145,260 L165,260 L160,280 L145,280 Z' },
    { name: 'Left Groin', d: 'M135,260 L115,260 L120,280 L135,280 Z' },
    { name: 'Right Upper Leg', d: 'M160,280 L145,280 L145,380 L160,380 Z' },
    { name: 'Left Upper Leg', d: 'M120,280 L135,280 L135,380 L120,380 Z' },
    { name: 'Right Knee', d: 'M160,380 L145,380 L145,410 L160,410 Z' },
    { name: 'Left Knee', d: 'M120,380 L135,380 L135,410 L120,410 Z' },
    { name: 'Right Lower Leg', d: 'M160,410 L145,410 L145,500 L160,500 Z' },
    { name: 'Left Lower Leg', d: 'M120,410 L135,410 L135,500 L120,500 Z' },
    { name: 'Right Foot', d: 'M160,500 L145,500 L150,530 L165,525 Z' },
    { name: 'Left Foot', d: 'M120,500 L135,500 L130,530 L115,525 Z' },
  ];

  const backParts = [
      { name: 'Back of Head', d: 'M140,40 C120,40 100,60 100,80 C100,110 120,130 140,130 C160,130 180,110 180,80 C180,60 160,40 140,40 Z' },
      { name: 'Back of Right Neck', d: 'M150,130 L160,130 L170,145 L160,145 Z' },
      { name: 'Back of Left Neck', d: 'M130,130 L120,130 L110,145 L120,145 Z' },
      { name: 'Upper Back', d: 'M120,145 L160,145 L170,220 L110,220 Z' },
      { name: 'Lower Back', d: 'M110,220 L170,220 L165,260 L115,260 Z' },
      { name: 'Back of Right Shoulder', d: 'M170,145 L160,145 L180,155 L190,150 Z' },
      { name: 'Back of Left Shoulder', d: 'M110,145 L120,145 L100,155 L90,150 Z' },
      { name: 'Back of Right Upper Arm', d: 'M190,150 L180,155 L190,210 L200,205 Z' },
      { name: 'Back of Left Upper Arm', d: 'M90,150 L100,155 L90,210 L80,205 Z' },
      { name: 'Back of Right Elbow', d: 'M200,205 L190,210 L195,225 L205,220 Z' },
      { name: 'Back of Left Elbow', d: 'M80,205 L90,210 L85,225 L75,220 Z' },
      { name: 'Back of Right Lower Arm', d: 'M205,220 L195,225 L200,280 L210,275 Z' },
      { name: 'Back of Left Lower Arm', d: 'M75,220 L85,225 L80,280 L70,275 Z' },
      { name: 'Back of Right Hand', d: 'M210,275 L200,280 L205,310 L215,305 Z' },
      { name: 'Back of Left Hand', d: 'M70,275 L80,280 L75,310 L65,305 Z' },
      { name: 'Buttocks', d: 'M115,260 L165,260 L160,290 L120,290 Z' },
      { name: 'Back of Right Upper Leg', d: 'M160,290 L145,290 L145,380 L160,380 Z' },
      { name: 'Back of Left Upper Leg', d: 'M120,290 L135,290 L135,380 L120,380 Z' },
      { name: 'Back of Right Knee', d: 'M160,380 L145,380 L145,410 L160,410 Z' },
      { name: 'Back of Left Knee', d: 'M120,380 L135,380 L135,410 L120,410 Z' },
      { name: 'Right Calf', d: 'M160,410 L145,410 L145,500 L160,500 Z' },
      { name: 'Left Calf', d: 'M120,410 L135,410 L135,500 L120,500 Z' },
      { name: 'Right Heel', d: 'M160,500 L145,500 L150,530 L165,525 Z' },
      { name: 'Left Heel', d: 'M120,500 L135,500 L130,530 L115,525 Z' },
  ];

  return (
    <svg width="250" height="500" viewBox="0 0 280 560" className="drop-shadow-md mx-auto">
      {/* FIX: Use a render function passed to map to avoid component-related typing errors */}
      {view === 'front' && frontParts.map(renderBodyPart)}
      {view === 'back' && backParts.map(renderBodyPart)}
    </svg>
  );
};


// --- IOPanelContent Component ---

interface IOPanelContentProps {
  employeeNumber: string;
  patientId: string;
  onSaveSuccess: (record: Partial<HealthRecord>) => void;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
  setSavingProgress: (msg: string | null) => void;
}

const IOPanelContent: React.FC<IOPanelContentProps> = ({
  employeeNumber,
  patientId,
  onSaveSuccess,
  isSaving,
  setIsSaving,
  setSavingProgress,
}) => {
  return (
    <div className="space-y-6">
      {/* Input (Food/Feeding) Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-200">
          Input (Food/Feeding)
        </h3>
        <InputFoodForm
          employeeNumber={employeeNumber}
          patientId={patientId}
          onSaveSuccess={onSaveSuccess}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          setSavingProgress={setSavingProgress}
        />
      </div>

      {/* Divider */}
      <hr className="border-gray-300" />

      {/* Output (Stool/Urine) Section */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3 pb-1 border-b border-gray-200">
          Output (Stool/Urine)
        </h3>
        <OutputForm
          employeeNumber={employeeNumber}
          patientId={patientId}
          onSaveSuccess={onSaveSuccess}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          setSavingProgress={setSavingProgress}
        />
      </div>
    </div>
  );
};

// --- Type Definitions for InputFoodForm ---

interface FoodItem {
  id: string;          // unique key for React list rendering
  name: string;        // food name (user-entered)
  amount: number;      // quantity
  unitCode: UnitCode;  // 0 | 1 | 2
}

type UnitCode = 0 | 1 | 2;  // 0=unit, 1=ml, 2=l
type FeedingType = 1 | 2 | 3;  // 1=Normal, 2=NGT, 3=Peg Tube
type MealCategory = 1 | 2 | 3 | 4 | 5 | 6;  // 1=Breakfast...6=Snack

interface InputFoodFormState {
  feedingType: FeedingType;
  mealCategory: MealCategory | null;
  foodItems: FoodItem[];
  notes: string;
}

// --- InputFoodForm Component ---

const InputFoodForm: React.FC<IOPanelContentProps> = ({
  employeeNumber,
  patientId,
  onSaveSuccess,
  isSaving,
  setIsSaving,
  setSavingProgress,
}) => {
  const [formState, setFormState] = useState<InputFoodFormState>({
    feedingType: 1,
    mealCategory: null,
    foodItems: [],
    notes: '',
  });

  // State for new food item inputs
  const [newItemName, setNewItemName] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [newItemUnit, setNewItemUnit] = useState<UnitCode>(0);

  // Validation errors state
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate meal category is selected
    if (formState.mealCategory === null) {
      newErrors['mealCategory'] = 'Please select a meal category';
    }

    // Validate at least one food item exists
    if (formState.foodItems.length === 0) {
      newErrors['foodItems'] = 'Please add at least one food item';
    }

    // Validate each food item has non-empty name and amount > 0
    formState.foodItems.forEach((item) => {
      if (!item.name.trim()) {
        newErrors[`foodItem_${item.id}_name`] = 'Food name is required';
      }
      if (item.amount <= 0) {
        newErrors[`foodItem_${item.id}_amount`] = 'Amount must be greater than 0';
      }
    });

    // Validate notes length
    if (formState.notes.length > 500) {
      newErrors['notes'] = 'Notes cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear errors when user corrects fields
  useEffect(() => {
    setErrors(prev => {
      const updated = { ...prev };
      // Clear mealCategory error when a category is selected
      if (formState.mealCategory !== null && updated['mealCategory']) {
        delete updated['mealCategory'];
      }
      // Clear foodItems error when items are added
      if (formState.foodItems.length > 0 && updated['foodItems']) {
        delete updated['foodItems'];
      }
      // Clear notes error when within limit
      if (formState.notes.length <= 500 && updated['notes']) {
        delete updated['notes'];
      }
      // Clear individual food item errors for items that are now valid
      formState.foodItems.forEach((item) => {
        if (item.name.trim() && updated[`foodItem_${item.id}_name`]) {
          delete updated[`foodItem_${item.id}_name`];
        }
        if (item.amount > 0 && updated[`foodItem_${item.id}_amount`]) {
          delete updated[`foodItem_${item.id}_amount`];
        }
      });
      return updated;
    });
  }, [formState.mealCategory, formState.foodItems, formState.notes]);

  const handleFeedingTypeSelect = (value: FeedingType) => {
    setFormState(prev => ({ ...prev, feedingType: value }));
  };

  const handleMealCategorySelect = (value: MealCategory) => {
    setFormState(prev => ({ ...prev, mealCategory: value }));
  };

  const handleAddFoodItem = () => {
    const trimmedName = newItemName.trim();
    if (!trimmedName || !newItemAmount) return;
    const amount = parseFloat(newItemAmount);
    if (isNaN(amount) || amount <= 0) return;

    const newItem: FoodItem = {
      id: Date.now().toString(),
      name: trimmedName,
      amount,
      unitCode: newItemUnit,
    };

    setFormState(prev => ({
      ...prev,
      foodItems: [...prev.foodItems, newItem],
    }));

    // Reset new item inputs
    setNewItemName('');
    setNewItemAmount('');
    setNewItemUnit(0);
  };

  const handleRemoveFoodItem = (id: string) => {
    setFormState(prev => ({
      ...prev,
      foodItems: prev.foodItems.filter(item => item.id !== id),
    }));
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormState(prev => ({ ...prev, notes: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setSavingProgress('Saving food input...');

    try {
      const encodedInput = encodeFoodItems(
        formState.foodItems.map(item => ({
          name: item.name,
          amount: item.amount,
          unitCode: item.unitCode,
        }))
      );

      const now = new Date();
      const localRecordedAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

      const body = {
        payload: {
          diaryType: "input",
          created_by: parseInt(employeeNumber, 10),
          created_at: now.toISOString(),
          recorded_at: localRecordedAt,
          input: encodedInput,
          notes: formState.notes,
          type: String(formState.feedingType),
          meal: String(formState.mealCategory!),
        }
      };

      const response = await fetch(`${API_DOMAIN}/careadmin/customers/${patientId}/input`, {
        method: 'POST',
        headers: {
          'x-api-key': API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to save food input');
      }

      const successDate = new Date();
      const year = successDate.getFullYear();
      const month = String(successDate.getMonth() + 1).padStart(2, '0');
      const day = String(successDate.getDate()).padStart(2, '0');
      let hours = successDate.getHours();
      const minutes = String(successDate.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      const formattedDate = `${year}-${month}-${day} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;

      onSaveSuccess({
        id: `rec-${Date.now()}`,
        date: formattedDate,
        recordedBy: `Employee #${employeeNumber}`,
        type: 'Notes',
        summary: `Food Input: ${formState.foodItems.map(i => i.name).join(', ')}`,
      } as any);
    } catch (error) {
      alert('Failed to save food input. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingProgress(null);
    }
  };

  return (
    <>
      {/* Feeding Type Selector */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-2">Feeding Type</label>
        <div className="grid grid-cols-1 hp-sm:grid-cols-3 hp-md:grid-cols-3 hp-lg:grid-cols-3 hp-xl:grid-cols-3 gap-3">
          {feedingTypes.map((ft) => (
            <button
              key={ft.value}
              type="button"
              onClick={() => handleFeedingTypeSelect(ft.value as FeedingType)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex flex-col items-center gap-1 ${
                formState.feedingType === ft.value
                  ? 'bg-primary border-primary text-white'
                  : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
              }`}
            >
              <AssetIcon src={ft.icon} label={ft.label} className={iconSizeClass(iconToken('INPUT', 'panel'))} />
              <span className="text-xs">{ft.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Meal Category Selector */}
      <div className="space-y-2 mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Meal Category</label>
        <div className="grid grid-cols-2 hp-sm:grid-cols-3 hp-md:grid-cols-3 hp-lg:grid-cols-3 hp-xl:grid-cols-3 gap-3">
          {mealCategories.map((mc) => (
            <button
              key={mc.value}
              type="button"
              onClick={() => handleMealCategorySelect(mc.value as MealCategory)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex flex-col items-center gap-1 ${
                formState.mealCategory === mc.value
                  ? 'bg-primary border-primary text-white'
                  : errors['mealCategory']
                    ? 'bg-red-50 text-gray-700 border-red-500 hover:bg-red-100'
                    : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
              }`}
            >
              <AssetIcon src={mc.icon} label={mc.label} className={iconSizeClass(iconToken('INPUT', 'panel'))} />
              <span className="text-xs">{mc.label}</span>
            </button>
          ))}
        </div>
        {errors['mealCategory'] && <p className="text-red-500 text-xs mt-1">{errors['mealCategory']}</p>}
      </div>

      {/* Food Items Section */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Food Items</label>

        {/* Add New Food Item Row */}
        <div className="flex flex-wrap gap-2 mb-3">
          <input
            type="text"
            placeholder="Food name"
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            className="flex-1 min-w-[60px] max-w-[120px] px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-primary-light focus:border-primary"
          />
          <input
            type="number"
            placeholder="Amount"
            value={newItemAmount}
            onChange={(e) => setNewItemAmount(e.target.value)}
            className="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-primary-light focus:border-primary"
            min="0"
            step="any"
          />
          <select
            value={newItemUnit}
            onChange={(e) => setNewItemUnit(Number(e.target.value) as UnitCode)}
            className="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-primary-light focus:border-primary"
          >
            {unitCodes.map((uc) => (
              <option key={uc.value} value={uc.value}>
                {uc.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleAddFoodItem}
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors"
          >
            Add
          </button>
        </div>

        {/* Food Items List */}
        {formState.foodItems.length > 0 && (
          <ul className="space-y-2">
            {formState.foodItems.map((item) => (
              <li
                key={item.id}
                className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                  errors[`foodItem_${item.id}_name`] || errors[`foodItem_${item.id}_amount`]
                    ? 'bg-red-50 border border-red-500'
                    : 'bg-gray-50'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-800 break-words">
                    {item.name} — {item.amount} {unitCodes.find(u => u.value === item.unitCode)?.label || ''}
                  </span>
                  {errors[`foodItem_${item.id}_name`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`foodItem_${item.id}_name`]}</p>
                  )}
                  {errors[`foodItem_${item.id}_amount`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`foodItem_${item.id}_amount`]}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveFoodItem(item.id)}
                  className="flex-shrink-0 text-red-500 hover:text-red-700 text-sm font-medium transition-colors ml-2"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
        {errors['foodItems'] && <p className="text-red-500 text-xs mt-1">{errors['foodItems']}</p>}
      </div>

      {/* Notes Section */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
        <textarea
          value={formState.notes}
          onChange={handleNotesChange}
          maxLength={500}
          placeholder="Add any additional notes..."
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-primary-light focus:border-primary resize-none ${
            errors['notes'] ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          rows={3}
        />
        <div className="flex justify-between items-center mt-1">
          {errors['notes'] && <p className="text-red-500 text-xs">{errors['notes']}</p>}
          <span className={`text-xs ml-auto ${formState.notes.length > 500 ? 'text-red-500' : 'text-gray-500'}`}>
            {formState.notes.length}/500
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSaving}
        className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition-colors mt-4 ${
          isSaving
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-green-500 text-white hover:bg-green-600'
        }`}
      >
        Save Food Input
      </button>
    </>
  );
};

// --- Type Definitions for OutputForm ---

type OutputType = 1 | 2;  // 1=Stool, 2=Urine
type StoolSubType = 1 | 2 | 3 | 4;  // 1=Normal, 2=Diaper, 3=Colostomy, 4=Ileostomy
type UrineSubType = 1 | 2 | 3 | 4;  // 1=Normal, 2=Diaper, 3=CBD, 4=Urostomy

interface OutputFormState {
  outputType: OutputType;
  subType: number;  // StoolSubType or UrineSubType depending on outputType
  color: number | null;
  amount: number | null;  // 1=Large, 2=Moderate, 3=Small
  pattern: number | null;  // only for Stool (Bristol 1-7)
  notes: string;
}

// --- OutputForm Component ---

const OutputForm: React.FC<IOPanelContentProps> = ({
  employeeNumber,
  patientId,
  onSaveSuccess,
  isSaving,
  setIsSaving,
  setSavingProgress,
}) => {
  const [formState, setFormState] = useState<OutputFormState>({
    outputType: 1,
    subType: 1,
    color: null,
    amount: null,
    pattern: null,
    notes: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formState.color === null) {
      newErrors['color'] = 'Please select a color';
    }

    if (formState.amount === null) {
      newErrors['amount'] = 'Please select an amount';
    }

    if (formState.outputType === 1 && formState.pattern === null) {
      newErrors['pattern'] = 'Please select a pattern';
    }

    if (formState.notes.length > 500) {
      newErrors['notes'] = 'Notes cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear errors when user corrects fields
  useEffect(() => {
    setErrors(prev => {
      const updated = { ...prev };
      if (formState.color !== null && updated['color']) {
        delete updated['color'];
      }
      if (formState.amount !== null && updated['amount']) {
        delete updated['amount'];
      }
      if (formState.pattern !== null && updated['pattern']) {
        delete updated['pattern'];
      }
      if (formState.notes.length <= 500 && updated['notes']) {
        delete updated['notes'];
      }
      return updated;
    });
  }, [formState.color, formState.amount, formState.pattern, formState.notes]);

  const handleOutputTypeSelect = (type: OutputType) => {
    setFormState(prev => ({
      ...prev,
      outputType: type,
      subType: 1,  // Reset sub-type to Normal(1) when output type changes
    }));
  };

  const handleSubTypeSelect = (value: number) => {
    setFormState(prev => ({ ...prev, subType: value }));
  };

  const handleColorSelect = (value: number) => {
    setFormState(prev => ({ ...prev, color: value }));
  };

  const handleAmountSelect = (value: number) => {
    setFormState(prev => ({ ...prev, amount: value }));
  };

  const handlePatternSelect = (value: number) => {
    setFormState(prev => ({ ...prev, pattern: value }));
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormState(prev => ({ ...prev, notes: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    setSavingProgress('Saving output record...');

    try {
      const now = new Date();
      const localRecordedAt = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}T${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

      const innerPayload: Record<string, any> = {
        diaryType: "output",
        created_by: parseInt(employeeNumber, 10),
        created_at: now.toISOString(),
        recorded_at: localRecordedAt,
        output: String(formState.outputType),
        type: String(formState.subType),
        amount: String(formState.amount),
        color: String(formState.color),
        notes: formState.notes,
      };

      // Include pattern only for Stool (outputType === 1)
      if (formState.outputType === 1) {
        innerPayload.pattern = String(formState.pattern);
      }

      const body = { payload: innerPayload };

      const response = await fetch(`${API_DOMAIN}/careadmin/customers/${patientId}/output`, {
        method: 'POST',
        headers: {
          'x-api-key': API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to save output record');
      }

      const outputLabel = formState.outputType === 1 ? 'Stool' : 'Urine';
      const nowDate = new Date();
      const yr = nowDate.getFullYear();
      const mo = String(nowDate.getMonth() + 1).padStart(2, '0');
      const dy = String(nowDate.getDate()).padStart(2, '0');
      let hr = nowDate.getHours();
      const mn = String(nowDate.getMinutes()).padStart(2, '0');
      const ap = hr >= 12 ? 'PM' : 'AM';
      hr = hr % 12 || 12;
      const fmtDate = `${yr}-${mo}-${dy} ${String(hr).padStart(2, '0')}:${mn} ${ap}`;

      onSaveSuccess({
        id: `rec-${Date.now()}`,
        date: fmtDate,
        recordedBy: `Employee #${employeeNumber}`,
        type: 'Notes',
        summary: `${outputLabel} Output recorded`,
      } as any);
    } catch (error) {
      alert('Failed to save output record. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingProgress(null);
    }
  };

  const currentSubTypes = formState.outputType === 1 ? stoolSubTypes : urineSubTypes;
  const currentColors = formState.outputType === 1 ? stoolColors : urineColors;

  return (
    <div className="space-y-4">
      {/* Output Type Toggle */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Output Type</label>
        <div className="grid grid-cols-2 hp-sm:grid-cols-2 hp-md:grid-cols-2 hp-lg:grid-cols-2 hp-xl:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleOutputTypeSelect(1)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex flex-col items-center gap-1 ${
              formState.outputType === 1
                ? 'bg-primary border-primary text-white'
                : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
            }`}
          >
            <AssetIcon src={outputClassifications[0]?.icon} label="Stool" className={iconSizeClass(iconToken('OUTPUT', 'panel'))} />
            <span className="text-xs">Stool</span>
          </button>
          <button
            type="button"
            onClick={() => handleOutputTypeSelect(2)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex flex-col items-center gap-1 ${
              formState.outputType === 2
                ? 'bg-primary border-primary text-white'
                : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
            }`}
          >
            <AssetIcon src={outputClassifications[1]?.icon} label="Urine" className={iconSizeClass(iconToken('OUTPUT', 'panel'))} />
            <span className="text-xs">Urine</span>
          </button>
        </div>
      </div>

      {/* Sub-Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {formState.outputType === 1 ? 'Stool Type' : 'Urine Type'}
        </label>
        <div className="grid grid-cols-2 hp-sm:grid-cols-2 hp-md:grid-cols-2 hp-lg:grid-cols-2 hp-xl:grid-cols-2 gap-3">
          {currentSubTypes.map((st) => (
            <button
              key={st.value}
              type="button"
              onClick={() => handleSubTypeSelect(st.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border flex flex-col items-center gap-1 ${
                formState.subType === st.value
                  ? 'bg-primary border-primary text-white'
                  : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
              }`}
            >
              {st.icon && <AssetIcon src={st.icon} label={st.label} className={iconSizeClass(iconToken('OUTPUT', 'panel'))} />}
              <span className="text-xs">{st.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Urine Classification Reference Image - Only for Urine */}
      {formState.outputType === 2 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Urine Classification Reference</label>
          <img 
            src="/health_diary/urine/urine_classification_info.png"
            alt="Urine classification reference chart" 
            className="w-full max-w-full h-auto rounded-lg border border-light-gray"
          />
        </div>
      )}

      {/* Color Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
        <div className="flex flex-wrap gap-2">
          {currentColors.map((color) => (
            <button
              key={color.value}
              type="button"
              onClick={() => handleColorSelect(color.value)}
              className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-colors ${
                formState.color === color.value
                  ? 'border-primary bg-primary-light'
                  : 'border-transparent hover:border-gray-300'
              }`}
            >
              <div
                className="w-8 h-8 rounded-full border border-gray-300"
                style={{ backgroundColor: color.hex }}
              />
              <span className="text-xs text-gray-600">{color.label}</span>
            </button>
          ))}
        </div>
        {errors['color'] && <p className="text-red-500 text-xs mt-1">{errors['color']}</p>}
      </div>

      {/* Amount Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Amount</label>
        <div className="grid grid-cols-3 gap-2">
          {outputAmounts.map((amt) => (
            <button
              key={amt.value}
              type="button"
              onClick={() => handleAmountSelect(amt.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                formState.amount === amt.value
                  ? 'bg-primary border-primary text-white'
                  : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
              }`}
            >
              {amt.label}
            </button>
          ))}
        </div>
        {errors['amount'] && <p className="text-red-500 text-xs mt-1">{errors['amount']}</p>}
      </div>

      {/* Bristol Stool Chart Pattern - Only for Stool */}
      {formState.outputType === 1 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Bristol Stool Chart</label>
          <div className="grid grid-cols-1 hp-sm:grid-cols-2 hp-md:grid-cols-2 hp-lg:grid-cols-2 hp-xl:grid-cols-2 gap-3">
            {bristolStoolTypes.map((bst) => (
              <button
                key={bst.value}
                type="button"
                onClick={() => handlePatternSelect(bst.value)}
                className={`px-2 py-1.5 rounded-lg text-xs transition-colors border text-left flex items-center gap-2 ${
                  formState.pattern === bst.value
                    ? 'bg-primary border-primary text-white'
                    : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
                }`}
              >
                <AssetIcon src={bst.icon} label={bst.label} className={`${iconSizeClass(iconToken('OUTPUT', 'panel'))} flex-shrink-0`} />
                <div>
                  <span className="font-medium">{bst.label}:</span> {bst.description}
                </div>
              </button>
            ))}
          </div>
          {errors['pattern'] && <p className="text-red-500 text-xs mt-1">{errors['pattern']}</p>}
        </div>
      )}

      {/* Notes Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Notes (optional)</label>
        <textarea
          value={formState.notes}
          onChange={handleNotesChange}
          maxLength={500}
          placeholder="Add any additional notes..."
          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-primary-light focus:border-primary resize-none ${
            errors['notes'] ? 'border-red-500 bg-red-50' : 'border-gray-300'
          }`}
          rows={3}
        />
        <div className="flex justify-between items-center mt-1">
          {errors['notes'] && <p className="text-red-500 text-xs">{errors['notes']}</p>}
          <span className={`text-xs ml-auto ${formState.notes.length > 500 ? 'text-red-500' : 'text-gray-500'}`}>
            {formState.notes.length}/500
          </span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={isSaving}
        className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
          isSaving
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : 'bg-green-500 text-white hover:bg-green-600'
        }`}
      >
        Save Output
      </button>
    </div>
  );
};


const HealthInputPanel: React.FC<HealthInputPanelProps> = ({ isOpen, onClose, onAddRecord, employeeNumber, patientId }) => {
  const [openAccordion, setOpenAccordion] = useState<HealthCategory | null>(null);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [isCameraOpen, setIsCameraOpen] = useState<boolean>(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [cameraFacingMode, setCameraFacingMode] = useState<'environment' | 'user'>('environment');
  const [isCapturingForPhysio, setIsCapturingForPhysio] = useState(false);
  const [selectedADL, setSelectedADL] = useState<string[]>([]);
  const [selectedIO, setSelectedIO] = useState<string[]>([]);
  const [selectedEmotion, setSelectedEmotion] = useState<string | null>(null);
  const [gcsScores, setGcsScores] = useState<{ eye: number | null; verbal: number | null; motor: number | null }>({
    eye: null,
    verbal: null,
    motor: null,
  });
  const [gcsNotes, setGcsNotes] = useState('');

  // State for Pain
  const [painLevel, setPainLevel] = useState<string | null>(null);
  const [painLocations, setPainLocations] = useState<string[]>([]);
  const [painNotes, setPainNotes] = useState('');
  const [bodyView, setBodyView] = useState<'front' | 'back'>('front');


  // State for input values and validation
  const [vitals, setVitals] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [noteImportant, setNoteImportant] = useState<boolean>(false);
  const [importantNoteText, setImportantNoteText] = useState<string>('');
  const [importantNoteImportant, setImportantNoteImportant] = useState<boolean>(true);
  const [ioValues, setIoValues] = useState<Record<string, string>>({});
  // Simplified physio state
  const [physioPainOnMovement, setPhysioPainOnMovement] = useState<number>(0);
  const [physioActivities, setPhysioActivities] = useState<string[]>([]);
  const [physioNotes, setPhysioNotes] = useState('');
  const [physioPhotos, setPhysioPhotos] = useState<string[]>([]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState<HealthCategory | null>(null);
  const [savingProgress, setSavingProgress] = useState<string | null>(null);


  const fileInputRef = useRef<HTMLInputElement>(null);
  const physioFileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const validate = useCallback((
    currentVitals: Record<string, string>,
    currentNotes: string,
    currentIoValues: Record<string, string>,
    currentSelectedIO: string[]
  ): Record<string, string> => {
    const newErrors: Record<string, string> = {};

    // Vitals Validation
    for (const label in VITAL_VALIDATIONS) {
        const valueStr = currentVitals[label];
        if (valueStr === undefined || valueStr === '') continue; // Not validating empty fields, only filled ones

        const value = parseFloat(valueStr);
        const rules = VITAL_VALIDATIONS[label];

        if (isNaN(value)) {
            newErrors[label] = 'Must be a number.';
            continue;
        }

        if (rules.min !== undefined && value < rules.min) {
            newErrors[label] = `Value must be at least ${rules.min}.`;
        } else if (rules.max !== undefined && value > rules.max) {
            newErrors[label] = `Value cannot exceed ${rules.max}.`;
        } else if (rules.integer && !Number.isInteger(value)) {
            newErrors[label] = 'Must be a whole number.';
        }
    }
    
    const hasSystolic = currentVitals['BP Systolic'] && currentVitals['BP Systolic'].trim() !== '';
    const hasDiastolic = currentVitals['BP Diastolic'] && currentVitals['BP Diastolic'].trim() !== '';

    if (hasSystolic !== hasDiastolic) {
        if (hasSystolic) newErrors['BP Diastolic'] = 'Required with Systolic.';
        else newErrors['BP Systolic'] = 'Required with Diastolic.';
    } else if (hasSystolic && hasDiastolic) {
        const systolic = parseFloat(currentVitals['BP Systolic']);
        const diastolic = parseFloat(currentVitals['BP Diastolic']);
        if (!isNaN(systolic) && !isNaN(diastolic) && systolic <= diastolic) {
            if (!newErrors['BP Systolic']) newErrors['BP Systolic'] = 'Systolic must be > Diastolic.';
            if (!newErrors['BP Diastolic']) newErrors['BP Diastolic'] = 'Diastolic must be < Systolic.';
        }
    }

    // Notes Validation
    if (currentNotes.length > NOTE_CHAR_LIMIT) {
        newErrors.notes = `Cannot exceed ${NOTE_CHAR_LIMIT} characters.`;
    }

    // IO Validation
    currentSelectedIO.forEach(id => {
        const valueStr = currentIoValues[id];
        if (valueStr !== undefined && valueStr.trim() !== '') {
            const value = parseFloat(valueStr);
            if (isNaN(value) || value <= 0) {
                 newErrors[id] = 'Must be a positive number.';
            } else if (id === 'stool' && !Number.isInteger(value)) {
                 newErrors[id] = 'Must be a whole number.';
            }
        }
    });

    return newErrors;
  }, []);
  
  // Live validation for input formats
  useEffect(() => {
    const formatErrors = validate(vitals, notes, ioValues, selectedIO);
    setErrors(prevErrors => {
        const newErrors = {...prevErrors};
        // Clear previous format errors
        Object.keys(VITAL_VALIDATIONS).forEach(k => delete newErrors[k]);
        delete newErrors.notes;
        ioCategories.flatMap(c => c.items).forEach(i => delete newErrors[i.id]);

        // Add new format errors
        return {...newErrors, ...formatErrors};
    });
  }, [vitals, notes, ioValues, selectedIO, validate]);

  useEffect(() => {
    // Cleanup object URLs on unmount to prevent memory leaks
    return () => {
      photoPreviews.forEach(url => {
        if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url)
        }
      });
      physioPhotos.forEach(url => {
        if (url.startsWith('blob:')) {
            URL.revokeObjectURL(url)
        }
      });
    };
  }, [photoPreviews, physioPhotos]);
  
  // Effect to manage the camera stream lifecycle
  useEffect(() => {
    if (!isCameraOpen) {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
      return;
    }

    let isCancelled = false;

    const getStream = async () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const constraints = { video: { facingMode: cameraFacingMode } };
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!isCancelled) {
          setStream(mediaStream);
        }
      } catch (err) {
        console.error(`Error opening camera with mode ${cameraFacingMode}:`, err);
        if (!isCancelled && cameraFacingMode === 'environment') {
          console.log("Falling back to user camera");
          setCameraFacingMode('user'); // This will re-trigger the effect
        } else if (!isCancelled) {
          alert("Could not access camera. Please check permissions.");
          setIsCameraOpen(false);
        }
      }
    };

    getStream();

    return () => {
      isCancelled = true;
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
        setStream(null);
      }
    };
  }, [isCameraOpen, cameraFacingMode]);
  
  useEffect(() => {
    if (isCameraOpen && stream && videoRef.current) {
        videoRef.current.srcObject = stream;
    }
  }, [isCameraOpen, stream]);

  const handleAccordionClick = (category: HealthCategory) => {
    setOpenAccordion(openAccordion === category ? null : category);
  };

  const handleADLSelect = (activityLabel: string) => {
    setSelectedADL(prev =>
        prev.includes(activityLabel)
            ? prev.filter(item => item !== activityLabel)
            : [...prev, activityLabel]
    );
  };
  
  const handleIOSelect = (itemId: string) => {
    setSelectedIO(prev =>
        prev.includes(itemId)
            ? prev.filter(item => item !== itemId)
            : [...prev, itemId]
    );
  };

  const handleEmotionSelect = (emotionLabel: string) => {
    setSelectedEmotion(prev => (prev === emotionLabel ? null : emotionLabel));
  };

  const handleGcsSelect = (category: 'eye' | 'verbal' | 'motor', score: number) => {
    setGcsScores(prev => ({
        ...prev,
        [category]: prev[category] === score ? null : score,
    }));
  };
  
  const handlePainLocationSelect = (location: string) => {
    setPainLocations(prev =>
      prev.includes(location)
        ? prev.filter(item => item !== location)
        : [...prev, location]
    );
  };

  const totalGcsScore = (gcsScores.eye || 0) + (gcsScores.verbal || 0) + (gcsScores.motor || 0);

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>, isPhysio: boolean = false) => {
    const setPreviews = isPhysio ? setPhysioPhotos : setPhotoPreviews;
    const previews = isPhysio ? physioPhotos : photoPreviews;

    if (event.target.files) {
      const files = Array.from(event.target.files);
      const remainingSlots = 6 - previews.length;
      
      const filesToAdd = files.slice(0, remainingSlots);
      const newPreviews = filesToAdd.map(file => URL.createObjectURL(file as Blob));

      setPreviews(prev => [...prev, ...newPreviews]);
    }
    
    if (event.target) {
        (event.target as HTMLInputElement).value = "";
    }
  };
  
  const handleVitalChange = (label: string, value: string) => {
    setVitals(prev => ({ ...prev, [label]: value }));
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNotes(event.target.value);
  };

  const handleIOValueChange = (id: string, value: string) => {
    setIoValues(prev => ({...prev, [id]: value}));
  };

  const removePhoto = (index: number, isPhysio: boolean = false) => {
    const setPreviews = isPhysio ? setPhysioPhotos : setPhotoPreviews;
    const previews = isPhysio ? physioPhotos : photoPreviews;
    
    const url = previews[index];
    if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url); // Revoke URL before removing from state
    }
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const triggerFileUpload = (isPhysio: boolean = false) => {
    const ref = isPhysio ? physioFileInputRef : fileInputRef;
    ref.current?.click();
  };

  const handleOpenCamera = (isPhysio: boolean) => {
    const previews = isPhysio ? physioPhotos : photoPreviews;
    if (previews.length >= 6) return;
    
    setIsCapturingForPhysio(isPhysio);
    setCameraFacingMode('environment'); // Always default to back camera
    setIsCameraOpen(true);
  };

  const closeCamera = () => {
    setIsCameraOpen(false);
  };
  
  const switchCamera = () => {
    setCameraFacingMode(prevMode => (prevMode === 'environment' ? 'user' : 'environment'));
  };

  const handleCapture = () => {
      if (videoRef.current && canvasRef.current) {
          const video = videoRef.current;
          const canvas = canvasRef.current;
          const context = canvas.getContext('2d');
          const previews = isCapturingForPhysio ? physioPhotos : photoPreviews;
          const setPreviews = isCapturingForPhysio ? setPhysioPhotos : setPhotoPreviews;

          if(context && previews.length < 6) {
              canvas.width = video.videoWidth;
              canvas.height = video.videoHeight;
              context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
              const dataUrl = canvas.toDataURL('image/jpeg');
              setPreviews(prev => [...prev, dataUrl]);
              closeCamera();
          }
      }
  };

  const handlePhysioActivitySelect = (activityLabel: string) => {
    setPhysioActivities(prev =>
        prev.includes(activityLabel)
            ? prev.filter(item => item !== activityLabel)
            : [...prev, activityLabel]
    );
  };
  
  const checkCompleteness = useCallback((category: HealthCategory) => {
    const newErrors: Record<string, string> = {};
    switch (category) {
        case HealthCategory.ADL:
            if (selectedADL.length === 0) newErrors.adl = 'Please select at least one activity.';
            break;
        case HealthCategory.INPUT:
        case HealthCategory.OUTPUT:
            // Validation handled internally by InputFoodForm / OutputForm
            break;
        case HealthCategory.EMOTIONS:
            if (!selectedEmotion) newErrors.emotion = 'Please select an emotion.';
            break;
        case HealthCategory.VITALS:
            if (Object.values(vitals).every(v => String(v ?? '').trim() === '')) {
                newErrors.vitals = 'Please enter at least one vital sign.';
            }
            break;
        case HealthCategory.GCS:
            if (gcsScores.eye === null) newErrors.gcs_eye = 'Please select an eye opening response.';
            if (gcsScores.verbal === null) newErrors.gcs_verbal = 'Please select a verbal response.';
            if (gcsScores.motor === null) newErrors.gcs_motor = 'Please select a motor response.';
            break;
        case HealthCategory.PAIN:
            if (!painLevel) newErrors.pain_level = 'Please select a pain level.';
            if (painLocations.length === 0) newErrors.pain_location = 'Please select at least one pain location on the diagram.';
            break;
        case HealthCategory.NOTES:
            if (notes.trim().length === 0) newErrors.notes_required = 'Note cannot be empty.';
            break;
        case HealthCategory.IMPORTANT_NOTE:
            if (importantNoteText.trim().length === 0) newErrors.important_note_required = 'Important note cannot be empty.';
            break;
        case HealthCategory.PHOTOS:
            if (photoPreviews.length === 0) newErrors.photos = 'Please upload or take at least one photo.';
            break;
        case HealthCategory.PHYSIO:
            if (physioActivities.length === 0 && physioPainOnMovement === 0 && physioNotes.trim() === '' && physioPhotos.length === 0) {
                newErrors.physio = 'Please fill in at least one part of the physiotherapy record.';
            }
            break;
    }
    return newErrors;
  }, [selectedADL, selectedIO, ioValues, selectedEmotion, vitals, gcsScores, painLevel, painLocations, notes, importantNoteText, photoPreviews, physioActivities, physioPainOnMovement, physioNotes, physioPhotos]);


  const handleSave = useCallback(async (category: HealthCategory) => {
    const formatErrors = validate(vitals, notes, ioValues, selectedIO);
    const completenessErrors = checkCompleteness(category);
    const allErrors = { ...formatErrors, ...completenessErrors };

    if (Object.keys(allErrors).length > 0) {
        setErrors(allErrors);
        return;
    }

    setErrors({});
    
    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const strHours = hours.toString().padStart(2, '0');
        return `${year}-${month}-${day} ${strHours}:${minutes} ${ampm}`;
    };

    const mapCategoryToRecordType = (cat: HealthCategory): HealthRecord['type'] => {
        switch (cat) {
            case HealthCategory.ADL: return 'ADL';
            case HealthCategory.EMOTIONS: return 'Emotion';
            case HealthCategory.VITALS: return 'Vitals';
            case HealthCategory.PHOTOS: return 'Photos';
            case HealthCategory.PHYSIO: return 'Physiotherapy';
            case HealthCategory.APPOINTMENTS: return 'Appointment';
            case HealthCategory.GCS: return 'Glasgow Coma Scale (GCS)';
            case HealthCategory.PAIN: return 'Pain';
            case HealthCategory.IMPORTANT_NOTE: return 'Notes';
            default: // For IO and NOTES itself, use the 'Notes' type.
                return 'Notes';
        }
    };
    
    setIsSaving(category);
    setSavingProgress(null);
    try {
        const newRecord: Partial<HealthRecord> = {
            id: `rec-${new Date().getTime()}`,
            date: formatDate(new Date()),
            recordedBy: `Employee #${employeeNumber}`,
            type: mapCategoryToRecordType(category),
        };
        
        let summary = '';
        let successAction = () => {};
        let shouldSave = true;
        let isApiHandled = false;

        if (category === HealthCategory.ADL) {
            if (selectedADL.length === 0) {
                shouldSave = false;
            } else {
                 const adlApiMap: { [key: string]: string } = {
                    'Activity': 'activity', 'Ambulation': 'ambulation', 'Bathing': 'bathing',
                    'Bed bath': 'bed bath', 'Change Clothes': 'changeclothes', 'Change Linen': 'changelinen',
                    'Exercise': 'exercise', 'Face Massage': 'face massage', 'Facial': 'facial',
                    'Feed Food': 'feed food', 'Haircut': 'haircut', 'Head Massage': 'head massage',
                    'Body Massage': 'body massage', 'Foot Massage': 'foot massage', 'Nailcare': 'nailcare',
                    'Shaving': 'shaving', 'Toilet Assist': 'toiletassist', 'Turning': 'turning',
                    'Diaper Change': 'diaperchange', 'Apply Lotion': 'putlotion', 'Light Stretching': 'Light Stretching'
                };
                const apiCalls = selectedADL.map(activity => {
                    const apiActivity = adlApiMap[activity];
                    if (!apiActivity) {
                        console.warn(`No API mapping for ADL activity: ${activity}`);
                        return Promise.reject(new Error(`No mapping for ${activity}`));
                    }
                    const payload = {
                        customer_id: [parseInt(patientId, 10)],
                        adl: apiActivity,
                        table: "h_adl",
                        created_by: [parseInt(employeeNumber, 10)],
                        notes: ""
                    };
                    return fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                        method: 'PUT',
                        headers: {
                            'x-api-key': API_KEY,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(payload)
                    }).then(response => {
                        if (!response.ok) throw new Error(`API call failed for ${activity}`);
                        return response;
                    });
                });
    
                await Promise.all(apiCalls);
                summary = `Completed: ${selectedADL.join(', ')}.`;
                successAction = () => setSelectedADL([]);
                isApiHandled = true;
            }

        } else {
            let dataToSave: any = { category };
            switch (category) {
                case HealthCategory.VITALS:
                    const enteredVitals = Object.entries(vitals).filter(([_, value]) => String(value).trim() !== '');
                    
                    const vitalApiMap: Record<string, { key: string; table: string; unit: string; type: 'float' | 'integer' }> = {
                        'Weight (kg)': { key: 'weight', table: 'h_weight', unit: 'kg', type: 'float' },
                        'Heart Rate (bpm)': { key: 'heart_rate', table: 'h_heart_rate', unit: 'bpm', type: 'integer' },
                        'Glucose (mmol/L)': { key: 'glucose', table: 'h_glucose', unit: 'mmol/L', type: 'float' },
                        'Sleep (hrs)': { key: 'sleep', table: 'h_sleep', unit: 'hours', type: 'integer' },
                        'Temp (°C)': { key: 'temperature', table: 'h_temperature', unit: '°C', type: 'float' },
                        'SpO2 (%)': { key: 'oxygen', table: 'h_spo2', unit: '%', type: 'integer' },
                        'Respiration (bpm)': { key: 'respiratory', table: 'h_respiratory', unit: 'bpm', type: 'integer' },
                    };
                    
                    const apiCalls: { label: string, payload: any }[] = [];
                    const noteVitals: { label: string, value: string }[] = [];

                    for (const [label, valueStr] of enteredVitals) {
                        const mapping = vitalApiMap[label];
                        if (mapping) {
                            const value = mapping.type === 'integer' ? parseInt(String(valueStr), 10) : parseFloat(String(valueStr));
                            const payload = {
                                customer_id: [parseInt(patientId, 10)],
                                [mapping.key]: value,
                                unit: mapping.unit,
                                table: mapping.table,
                                created_by: [parseInt(employeeNumber, 10)],
                            };
                            apiCalls.push({ label, payload });
                        } else if (!label.startsWith('BP ')) {
                             // Collect vitals without a dedicated table to be saved as notes.
                             // Exclude BP parts as they are handled separately below.
                            noteVitals.push({ label, value: String(valueStr) });
                        }
                    }

                    const systolic = vitals['BP Systolic'];
                    const diastolic = vitals['BP Diastolic'];
                    if (systolic && diastolic) {
                        const payload = {
                            customer_id: [parseInt(patientId, 10)],
                            bp_systolic: parseInt(String(systolic), 10),
                            bp_distolic: parseInt(String(diastolic), 10),
                            unit: 'mmHg',
                            table: 'h_blood_pressure',
                            created_by: [parseInt(employeeNumber, 10)],
                        };
                        apiCalls.push({ label: 'Blood Pressure', payload });
                    }

                    // Create API call payloads for the note-based vitals
                    for (const { label, value } of noteVitals) {
                        const noteText = `${label.split(' (')[0]}: ${value}`;
                        const payload = {
                            customer_id: [parseInt(patientId, 10)],
                            notes: noteText,
                            table: 'h_note',
                            created_by: [parseInt(employeeNumber, 10)],
                        };
                        apiCalls.push({ label, payload });
                    }
    
                    if (apiCalls.length === 0) {
                        shouldSave = false;
                    } else {
                        isApiHandled = true;
                        const totalCalls = apiCalls.length;
                        for (let i = 0; i < totalCalls; i++) {
                            const { label, payload } = apiCalls[i];
                            setSavingProgress(`Saving ${label} (${i + 1} of ${totalCalls})...`);
                            const response = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                                method: 'PUT',
                                headers: {
                                    'x-api-key': API_KEY,
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify(payload)
                            });
                            if (!response.ok) {
                                const errorText = await response.text();
                                throw new Error(`API call failed for ${label}: ${errorText}`);
                            }
                        }
                        
                        let summaryParts: string[] = [];
                        const nonBpVitals = enteredVitals.filter(([label, _]) => !label.startsWith('BP '));
                        summaryParts = nonBpVitals.map(([key, value]) => `${key.split(' (')[0]}: ${value}`);
                        if (systolic && diastolic) {
                            summaryParts.unshift(`BP: ${systolic}/${diastolic}`);
                        }
                        summary = summaryParts.join(', ');

                        successAction = () => setVitals({});
                    }
                    break;
                case HealthCategory.INPUT:
                case HealthCategory.OUTPUT:
                    // Submission handled internally by InputFoodForm / OutputForm
                    shouldSave = false;
                    break;
                case HealthCategory.EMOTIONS:
                    if (!selectedEmotion) {
                        shouldSave = false;
                    } else {
                        const emotionApiMap: { [key: string]: string } = {
                            'Content': 'Content',
                            'Playful': 'Playful',
                            'Very Happy': 'Verry Happy',
                            'Happy': 'Happy',
                            'Restless': 'Restless',
                            'Sick': 'Sick',
                            'Angry': 'Angry',
                            'Annoyed': 'Annoyed',
                            'Confused': 'Confused',
                            'Sleepy': 'Sleepy',
                            'Upset': 'Upset',
                            'Sad': 'Sad',
                        };
                        const mentalValue = emotionApiMap[selectedEmotion];

                        if (mentalValue === undefined) {
                            throw new Error(`No API mapping for emotion: ${selectedEmotion}`);
                        }

                        const payload = {
                            customer_id: [parseInt(patientId, 10)],
                            mental: mentalValue,
                            table: "h_mental",
                            created_by: [parseInt(employeeNumber, 10)],
                            notes: ""
                        };

                        setSavingProgress('Saving emotion...');
                        const response = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                            method: 'PUT',
                            headers: {
                                'x-api-key': API_KEY,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(payload)
                        });

                        if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(`API call failed for Emotion: ${errorText}`);
                        }
                        const result = await response.json();
                        if (!result.success) {
                            throw new Error(`Error saving record:\nAPI call failed for Emotion: ${JSON.stringify(result)}`);
                        }

                        summary = `Patient feels ${selectedEmotion}.`;
                        successAction = () => setSelectedEmotion(null);
                        isApiHandled = true;
                    }
                    break;
                case HealthCategory.GCS:
                    if (totalGcsScore === 0) {
                        shouldSave = false;
                    } else {
                        const payload = {
                            customer_id: [parseInt(patientId, 10)],
                            eye: gcsCategories.eye.options.find(opt => opt.score === gcsScores.eye)?.label || "",
                            verbal: gcsCategories.verbal.options.find(opt => opt.score === gcsScores.verbal)?.label || "",
                            motor: gcsCategories.motor.options.find(opt => opt.score === gcsScores.motor)?.label || "",
                            score: totalGcsScore,
                            table: "h_gcs",
                            created_by: [parseInt(employeeNumber, 10)],
                            notes: gcsNotes.trim()
                        };
                
                        setSavingProgress('Saving GCS score...');
                        const response = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                            method: 'PUT',
                            headers: {
                                'x-api-key': API_KEY,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(payload)
                        });
                
                        if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(`API call failed for GCS: ${errorText}`);
                        }
                        const result = await response.json();
                        if (!result.success) {
                            throw new Error(`Error saving record:\nAPI call failed for GCS: ${JSON.stringify(result)}`);
                        }
                        
                        summary = `GCS Score: ${totalGcsScore} (E${gcsScores.eye || 'x'}V${gcsScores.verbal || 'x'}M${gcsScores.motor || 'x'}).`;
                        if (gcsNotes.trim()) {
                            summary += ` Note: ${gcsNotes.trim()}`;
                        }
                        
                        successAction = () => {
                            setGcsScores({ eye: null, verbal: null, motor: null });
                            setGcsNotes('');
                        };
                        isApiHandled = true;
                    }
                    break;
                case HealthCategory.PAIN:
                    if (!painLevel || painLocations.length === 0) {
                        shouldSave = false;
                        break;
                    }

                    isApiHandled = true;
                    setSavingProgress('Saving pain record...');

                    const painPayload = {
                        customer_id: parseInt(patientId, 10),
                        table: "h_pain",
                        created_by: parseInt(employeeNumber, 10),
                        pain: painLevel,
                        body: painLocations.join(', '),
                        notes: painNotes.trim()
                    };

                    const painResponse = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                        method: 'PUT',
                        headers: {
                            'x-api-key': API_KEY,
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(painPayload)
                    });

                    if (!painResponse.ok) {
                        const errorText = await painResponse.text();
                        throw new Error(`API call failed for Pain record: ${errorText}`);
                    }
                    const painResult = await painResponse.json();
                    if (!painResult.success) {
                        throw new Error(`Error saving record for Pain: ${JSON.stringify(painResult)}`);
                    }

                    const summaryParts = [
                        `Pain Level: ${painLevel}.`,
                        `Locations: ${painLocations.join(', ')}.`
                    ];
                    if (painNotes.trim()) {
                        summaryParts.push(`Notes: ${painNotes.trim()}`);
                    }
                    summary = summaryParts.join(' ');

                    successAction = () => {
                        setPainLevel(null);
                        setPainLocations([]);
                        setPainNotes('');
                    };
                    break;
                case HealthCategory.NOTES:
                    if (notes.trim().length === 0) {
                        shouldSave = false;
                    } else {
                        const notesPayload = buildImportantNoteInsert(
                            parseInt(patientId, 10),
                            parseInt(employeeNumber, 10),
                            notes,
                            noteImportant ? 1 : 0,
                        );

                        setSavingProgress('Saving note...');
                        const response = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                            method: 'PUT',
                            headers: {
                                'x-api-key': API_KEY,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(notesPayload)
                        });

                        if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(`API call failed for Notes: ${errorText}`);
                        }
                        const result = await response.json();
                        if (!result.success) {
                            throw new Error(`Error saving record:\nAPI call failed for Notes: ${JSON.stringify(result)}`);
                        }

                        summary = notes;
                        successAction = () => {
                            setNotes('');
                            setNoteImportant(false);
                        };
                        isApiHandled = true;
                    }
                    break;
                case HealthCategory.IMPORTANT_NOTE:
                    if (importantNoteText.trim().length === 0) {
                        shouldSave = false;
                    } else {
                        const importantPayload = buildImportantNoteInsert(
                            parseInt(patientId, 10),
                            parseInt(employeeNumber, 10),
                            importantNoteText,
                            importantNoteImportant ? 1 : 0,
                        );

                        setSavingProgress('Saving important note...');
                        const response = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                            method: 'PUT',
                            headers: {
                                'x-api-key': API_KEY,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(importantPayload)
                        });

                        if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(`API call failed for Important Note: ${errorText}`);
                        }
                        const result = await response.json();
                        if (!result.success) {
                            throw new Error(`Error saving record:\nAPI call failed for Important Note: ${JSON.stringify(result)}`);
                        }

                        summary = importantNoteText;
                        successAction = () => {
                            setImportantNoteText('');
                            setImportantNoteImportant(true);
                        };
                        isApiHandled = true;
                    }
                    break;
                case HealthCategory.PHOTOS:
                    if (photoPreviews.length === 0) {
                        shouldSave = false;
                        break;
                    }
                    isApiHandled = true;
                    
                    const imagePromisesForUpload = photoPreviews.map(p => {
                        if (p.startsWith('blob:')) {
                            return blobUrlToDataUrl(p);
                        }
                        return Promise.resolve(p); // Already a data URL
                    });
                    const dataUrls = await Promise.all(imagePromisesForUpload);

                    const totalCalls = dataUrls.length;
                    for (let i = 0; i < totalCalls; i++) {
                        const dataUrl = dataUrls[i];
                        
                        setSavingProgress(`Uploading photo ${i + 1} of ${totalCalls}...`);

                        const payload = {
                            customer_id: parseInt(patientId, 10),
                            table: "t_images",
                            created_by: [parseInt(employeeNumber, 10)],
                            notes: "",
                            image_base64: dataUrl
                        };

                        const response = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                            method: 'PUT',
                            headers: {
                                'x-api-key': API_KEY,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(payload)
                        });
                        
                        const result = await response.json();
                        if (!response.ok || !result.success) {
                            const errorMsg = result.error || JSON.stringify(result);
                            throw new Error(`API call failed for photo ${i + 1}: ${errorMsg}`);
                        }
                    }

                    summary = `${photoPreviews.length} photo(s) uploaded.`;
                    newRecord.images = dataUrls;
                    successAction = () => setPhotoPreviews([]);
                    break;
                case HealthCategory.PHYSIO:
                    const hasPhysioApiData = physioActivities.length > 0 || physioPainOnMovement > 0 || physioNotes.trim() !== '';

                    if (!hasPhysioApiData && physioPhotos.length === 0) {
                        shouldSave = false;
                        break;
                    }

                    if (hasPhysioApiData) {
                        const payload = {
                            customer_id: parseInt(patientId, 10),
                            table: "h_physio",
                            created_by: parseInt(employeeNumber, 10),
                            pain_of_movements: physioPainOnMovement,
                            type: physioActivities,
                            notes: physioNotes.trim()
                        };

                        setSavingProgress('Saving physiotherapy record...');
                        const response = await fetch(`${API_BASE_URL}?func=HcUpdateHealthJournal`, {
                            method: 'PUT',
                            headers: {
                                'x-api-key': API_KEY,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(payload)
                        });

                        if (!response.ok) {
                            const errorText = await response.text();
                            throw new Error(`API call failed for Physiotherapy: ${errorText}`);
                        }
                        const result = await response.json();
                        if (!result.success) {
                            throw new Error(`Error saving record for Physiotherapy: ${JSON.stringify(result)}`);
                        }
                        isApiHandled = true;
                    }
                    
                    // Build structured HTML summary for nice display
                    const physioHtmlParts: string[] = [];
                    if (physioActivities.length > 0) {
                        physioHtmlParts.push(`<div style="margin-bottom:6px"><span style="font-weight:600;color:#374151">Activities:</span> <span style="color:#5A46AA">${physioActivities.join(', ')}</span></div>`);
                    }
                    if (physioPainOnMovement > 0) {
                        const painColor = physioPainOnMovement >= 7 ? '#DC2626' : physioPainOnMovement >= 4 ? '#F59E0B' : '#059669';
                        physioHtmlParts.push(`<div style="margin-bottom:6px"><span style="font-weight:600;color:#374151">Pain on Movement:</span> <span style="font-weight:700;color:${painColor}">${physioPainOnMovement}/10</span></div>`);
                    }
                    if (physioNotes.trim()) {
                        // Notes may contain HTML from the rich text editor — keep it
                        physioHtmlParts.push(`<div style="margin-top:8px;padding:8px 12px;background:#F9FAFB;border-radius:6px;border-left:3px solid #5A46AA"><span style="font-weight:600;color:#374151;display:block;margin-bottom:4px;font-size:12px">Notes / Observations</span><div style="color:#4B5563;font-size:13px">${physioNotes.trim()}</div></div>`);
                    }
                    if (physioPhotos.length > 0) {
                        physioHtmlParts.push(`<div style="margin-top:6px;font-size:12px;color:#6B7280">📷 ${physioPhotos.length} photo(s) attached</div>`);
                    }

                    summary = physioHtmlParts.join('');
                    
                    const physioImagePromises = physioPhotos.map(p => p.startsWith('blob:') ? blobUrlToDataUrl(p) : Promise.resolve(p));
                    newRecord.images = await Promise.all(physioImagePromises);
                    
                    successAction = () => { 
                        setPhysioPainOnMovement(0); 
                        setPhysioActivities([]); 
                        setPhysioNotes(''); 
                        setPhysioPhotos([]); 
                    };
                    break;
                default:
                    shouldSave = false;
            }
             if (!isApiHandled && shouldSave) {
                console.log('Simulating API call to save data:', dataToSave);
                await new Promise(resolve => setTimeout(resolve, 500));
             }
        }

        if (!shouldSave) {
            setIsSaving(null);
            return;
        }

        newRecord.summary = summary;
        onAddRecord(newRecord as HealthRecord);
        successAction();

    } catch (error) {
        console.error('Error saving record:', error);
        alert('An error occurred while saving. Please try again.');
    } finally {
        setIsSaving(null);
        setSavingProgress(null);
    }
  }, [employeeNumber, patientId, onAddRecord, selectedADL, selectedIO, ioValues, selectedEmotion, vitals, gcsScores, totalGcsScore, gcsNotes, painLevel, painLocations, painNotes, notes, noteImportant, importantNoteText, importantNoteImportant, photoPreviews, physioPainOnMovement, physioActivities, physioNotes, physioPhotos, validate, checkCompleteness]);

  const resetState = useCallback((category: HealthCategory) => {
    switch (category) {
        case HealthCategory.ADL:
            setSelectedADL([]);
            break;
        case HealthCategory.INPUT:
        case HealthCategory.OUTPUT:
            // Reset handled internally by InputFoodForm / OutputForm
            break;
        case HealthCategory.EMOTIONS:
            setSelectedEmotion(null);
            break;
        case HealthCategory.VITALS:
            setVitals({});
            break;
        case HealthCategory.GCS:
            setGcsScores({ eye: null, verbal: null, motor: null });
            setGcsNotes('');
            break;
        case HealthCategory.PAIN:
            setPainLevel(null);
            setPainLocations([]);
            setPainNotes('');
            break;
        case HealthCategory.PHYSIO:
            setPhysioPainOnMovement(0);
            setPhysioActivities([]);
            setPhysioNotes('');
            setPhysioPhotos([]);
            break;
        case HealthCategory.NOTES:
            setNotes('');
            setNoteImportant(false);
            break;
        case HealthCategory.IMPORTANT_NOTE:
            setImportantNoteText('');
            setImportantNoteImportant(true);
            break;
        case HealthCategory.PHOTOS:
            setPhotoPreviews([]);
            break;
    }
    setErrors({});
  }, []);

  const renderButtons = (category: HealthCategory) => (
    <div className="sticky bottom-0 left-0 right-0 flex justify-end items-center gap-3 pt-4 mt-4 border-t border-light-gray bg-white">
        <button
            type="button"
            onClick={() => resetState(category)}
            className="px-4 py-2 rounded-lg font-medium bg-light-gray text-gray-700 hover:bg-gray-200 transition"
        >
            Reset
        </button>
        <button
            type="button"
            onClick={() => handleSave(category)}
            disabled={isSaving === category}
            className="px-6 py-2 rounded-lg font-medium bg-accent text-white hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
            {isSaving === category ? (
                <>
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {savingProgress || 'Saving...'}
                </>
            ) : 'Save'}
        </button>
    </div>
  );

  const PhotoUploader = ({ previews, onUpload, onRemove, onOpenCamera, onTriggerUpload, fileInputRef, maxPhotos = 6, error }: any) => (
    <div className={`p-2 rounded-lg transition-colors ${error ? 'bg-red-50 border border-red-300' : ''}`}>
        {error && <p className="text-red-600 text-sm mb-2 font-semibold">{error}</p>}
        <div className="space-y-4">
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {previews.map((src: string, index: number) => (
                    <div key={index} className="relative aspect-square group">
                        <img src={src} alt={`preview ${index}`} className="w-full h-full object-cover rounded-lg shadow-sm" />
                        <button
                            onClick={() => onRemove(index)}
                            className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove photo"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                ))}
            </div>
            
            {previews.length < maxPhotos && (
                <div className="pt-2">
                    <input type="file" ref={fileInputRef} onChange={onUpload} accept="image/*" multiple className="hidden" />
                    <div className="space-y-2">
                        <button
                            onClick={onOpenCamera}
                            disabled={previews.length >= maxPhotos}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold bg-primary text-white hover:bg-purple-700 transition disabled:opacity-50"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            <span>Use Camera</span>
                        </button>
                        <button
                            onClick={onTriggerUpload}
                            disabled={previews.length >= maxPhotos}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-light-gray text-primary hover:bg-primary-light transition disabled:opacity-50"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                            <span>Upload from Library</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    // Mobile/tablet (<881px / below hp-lg): full-viewport sheet (fixed inset-0).
    // Desktop (hp-lg+): fills the parent .side-panel slot.
    <div className="fixed inset-0 z-50 hp-lg:relative hp-lg:inset-auto hp-lg:z-auto hp-lg:h-full flex flex-col bg-light-bg">
      <header className="p-4 bg-white border-b border-light-gray flex items-center justify-between shadow-sm">
        <h2 className="text-xl font-bold text-primary">Health Information</h2>
        <button
          onClick={onClose}
          className="p-2 rounded-full text-gray-500 hover:bg-light-gray hover:text-gray-800"
          aria-label="Close panel"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      {/* Saving overlay - blocks all edits while save is in progress */}
      {isSaving && (
        <div className="absolute inset-0 bg-white bg-opacity-60 z-50 flex items-center justify-center" aria-live="polite">
          <div className="flex flex-col items-center gap-3 p-6 bg-white rounded-xl shadow-lg">
            <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-semibold text-gray-700">{savingProgress || 'Saving...'}</span>
          </div>
        </div>
      )}

      {/* Duplicate top Save button visible only in 600-880px band (hp-md to hp-lg) */}
      <div className="hidden hp-md:block hp-lg:hidden px-4 py-3 border-b border-light-gray bg-white">
        <button
          type="button"
          onClick={() => openAccordion && handleSave(openAccordion)}
          disabled={!openAccordion || isSaving === openAccordion}
          className="w-full px-6 py-2 rounded-lg font-medium bg-accent text-white hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {openAccordion && isSaving === openAccordion ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {savingProgress || 'Saving...'}
            </>
          ) : 'Save'}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        {healthCategories
          .filter(({ id }) => !openAccordion || openAccordion === id)
          .map(({ id, icon }) => {
            // Build AssetIcon element for PNG string icons; pass ReactNode SVGs directly
            const iconElement = typeof icon === 'string' ? (
              <AssetIcon src={icon} label={id} className={iconSizeClass(iconToken(id, 'nav'))} />
            ) : (
              icon
            );
            
            return (
            <AccordionCard
              key={id}
              title={id}
              icon={iconElement}
              isOpen={openAccordion === id}
              onClick={() => handleAccordionClick(id)}
            >
           {id === HealthCategory.ADL && (
              <div className={EXPANDED_PANEL_CLASS}>
                {errors.adl && <p className="text-red-600 text-sm mb-2 font-semibold">{errors.adl}</p>}
                <div className={`grid grid-cols-3 hp-sm:grid-cols-3 hp-md:grid-cols-4 hp-lg:grid-cols-4 hp-xl:grid-cols-4 gap-3 transition-colors ${errors.adl ? 'border-2 border-red-300 rounded-lg p-2 bg-red-50' : ''}`}>
                    {adlActivities.map(({ label, icon }) => (
                        <button
                            key={label}
                            onClick={() => handleADLSelect(label)}
                            className={`p-2 rounded-lg text-center transition-all duration-200 border-2 flex flex-col items-center justify-center aspect-square ${
                                selectedADL.includes(label) ? 'bg-primary border-primary text-white' : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
                            }`}
                        >
                            <AssetIcon src={icon} label={label} className={iconSizeClass(iconToken('ADL', 'panel'))} />
                            <span className="text-xs font-semibold mt-1">{label}</span>
                        </button>
                    ))}
                </div>
                {renderButtons(id)}
              </div>
           )}
           {id === HealthCategory.INPUT && (
            <div className={EXPANDED_PANEL_CLASS}>
              <InputFoodForm
                employeeNumber={employeeNumber}
                patientId={patientId}
                onSaveSuccess={(record) => onAddRecord(record as HealthRecord)}
                isSaving={isSaving === HealthCategory.INPUT}
                setIsSaving={(saving: boolean) => setIsSaving(saving ? HealthCategory.INPUT : null)}
                setSavingProgress={setSavingProgress}
              />
            </div>
           )}
           {id === HealthCategory.OUTPUT && (
            <div className={EXPANDED_PANEL_CLASS}>
              <OutputForm
                employeeNumber={employeeNumber}
                patientId={patientId}
                onSaveSuccess={(record) => onAddRecord(record as HealthRecord)}
                isSaving={isSaving === HealthCategory.OUTPUT}
                setIsSaving={(saving: boolean) => setIsSaving(saving ? HealthCategory.OUTPUT : null)}
                setSavingProgress={setSavingProgress}
              />
            </div>
           )}
           {id === HealthCategory.EMOTIONS && (
            <div className={EXPANDED_PANEL_CLASS}>
                 {errors.emotion && <p className="text-red-600 text-sm mb-2 font-semibold">{errors.emotion}</p>}
                 <div className={`grid grid-cols-3 hp-sm:grid-cols-3 hp-md:grid-cols-4 hp-lg:grid-cols-3 hp-xl:grid-cols-4 gap-3 transition-colors ${errors.emotion ? 'border-2 border-red-300 rounded-lg p-2 bg-red-50' : ''}`}>
                    {emotions.map(({ label, icon }) => (
                         <button
                            key={label}
                            onClick={() => handleEmotionSelect(label)}
                            className={`p-2 rounded-lg text-center transition-all duration-200 border-2 flex flex-col items-center justify-center aspect-square ${
                                selectedEmotion === label ? 'bg-primary border-primary text-white' : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
                            }`}
                        >
                            <AssetIcon src={icon} label={label} className={iconSizeClass(iconToken('EMOTIONS', 'panel'))} />
                            <span className="text-sm font-semibold mt-1">{label}</span>
                        </button>
                    ))}
                </div>
                {renderButtons(id)}
            </div>
           )}
           {id === HealthCategory.VITALS && (
                <div className={EXPANDED_PANEL_CLASS}>
                    <div className={`space-y-4 p-2 rounded-lg transition-colors ${errors.vitals ? 'bg-red-50 border border-red-300' : ''}`}>
                        {errors.vitals && <p className="text-red-600 text-sm font-semibold mb-2">{errors.vitals}</p>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {Object.keys(VITAL_VALIDATIONS).map(label => (
                                <div key={label}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <AssetIcon src={vitalIcons[label]} label={label} className={iconSizeClass(iconToken('VITALS', 'panel'))} />
                                      <label className="text-sm font-medium text-gray-700">{label}</label>
                                    </div>
                                    <input
                                        type="number"
                                        value={vitals[label] || ''}
                                        onChange={e => handleVitalChange(label, e.target.value)}
                                        className={`w-full p-2 border rounded-md bg-white text-gray-800 placeholder-gray-500 focus:ring-primary-light focus:border-primary ${errors[label] ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {errors[label] && <p className="text-red-500 text-xs mt-1">{errors[label]}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                    {renderButtons(id)}
                </div>
           )}
           {id === HealthCategory.GCS && (
                <div className={EXPANDED_PANEL_CLASS}>
                    <div className="space-y-6">
                    {Object.entries(gcsCategories).map(([key, data]) => (
                        <div key={key} className={`p-2 rounded-lg transition-colors ${errors[`gcs_${key}`] ? 'bg-red-50 border border-red-300' : ''}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <AssetIcon src={data.icon} label={data.title} className={iconSizeClass(iconToken('GCS', 'panel'))} />
                              <h4 className="font-semibold text-gray-700">{data.title}</h4>
                            </div>
                            {errors[`gcs_${key}`] && <p className="text-red-600 text-xs mb-2">{errors[`gcs_${key}`]}</p>}
                            <div className="space-y-2">
                                {data.options.map(opt => (
                                    <button
                                        key={opt.score}
                                        onClick={() => handleGcsSelect(key as 'eye' | 'verbal' | 'motor', opt.score)}
                                        className={`w-full text-left p-3 rounded-lg border-2 transition-colors flex justify-between items-center ${
                                            gcsScores[key as 'eye' | 'verbal' | 'motor'] === opt.score
                                            ? 'bg-primary border-primary text-white' : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
                                        }`}
                                    >
                                        <span className="font-medium">{opt.label}</span>
                                        <span className={`font-bold ${ gcsScores[key as 'eye' | 'verbal' | 'motor'] === opt.score ? 'text-white' : 'text-primary' }`}>{opt.score}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className="pt-4 border-t border-light-gray space-y-4">
                        <div className="text-right font-bold text-lg text-primary">
                            Total GCS Score: {totalGcsScore} / 15
                        </div>
                        <div>
                            <label htmlFor="gcs-notes" className="block text-sm font-medium text-gray-700 mb-1">Note (Optional)</label>
                            <textarea
                                id="gcs-notes"
                                value={gcsNotes}
                                onChange={(e) => setGcsNotes(e.target.value)}
                                rows={3}
                                placeholder="e.g., Patient seems drowsy but is responsive."
                                className="w-full p-2 border rounded-md bg-white text-gray-800 placeholder-gray-500 focus:ring-primary-light focus:border-primary border-gray-300"
                            />
                        </div>
                    </div>
                    {renderButtons(id)}
                    </div>
                </div>
           )}
            {id === HealthCategory.PAIN && (
                <div className={EXPANDED_PANEL_CLASS}>
                    <div className="space-y-6">
                    <div className={`p-2 rounded-lg transition-colors ${errors.pain_level ? 'bg-red-50 border border-red-300' : ''}`}>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Pain Level</label>
                        {errors.pain_level && <p className="text-red-600 text-xs mb-2">{errors.pain_level}</p>}
                        <div className="grid grid-cols-2 hp-sm:grid-cols-2 hp-md:grid-cols-3 hp-lg:grid-cols-3 hp-xl:grid-cols-3 gap-3">
                            {painLevels.map(({ label, icon }) => (
                                <button
                                    key={label}
                                    onClick={() => setPainLevel(label)}
                                    className={`p-4 rounded-lg font-semibold text-sm transition-colors border-2 flex flex-col items-center gap-1 ${
                                        painLevel === label ? 'bg-primary text-white border-primary' : 'bg-white hover:border-primary-light border-light-gray'
                                    }`}
                                >
                                    <AssetIcon src={icon} label={label} className={iconSizeClass(iconToken('PAIN', 'panel'))} />
                                    <span className="text-xs">{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={`p-2 rounded-lg transition-colors ${errors.pain_location ? 'bg-red-50 border border-red-300' : ''}`}>
                         <label className="block text-sm font-semibold text-gray-700 mb-2">Where is the pain?</label>
                         {errors.pain_location && <p className="text-red-600 text-xs mb-2">{errors.pain_location}</p>}
                        <div className="bg-white p-2 rounded-lg border border-light-gray">
                            <div className="flex justify-center mb-2">
                                <div className="bg-light-gray p-1 rounded-lg flex items-center">
                                    <button
                                        onClick={() => setBodyView('front')}
                                        className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${bodyView === 'front' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-primary'}`}
                                    >
                                        Front
                                    </button>
                                    <button
                                        onClick={() => setBodyView('back')}
                                        className={`px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ${bodyView === 'back' ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-primary'}`}
                                    >
                                        Back
                                    </button>
                                </div>
                            </div>
                            <HumanBodyDiagram selectedLocations={painLocations} onLocationClick={handlePainLocationSelect} view={bodyView} />
                        </div>
                    </div>

                    {painLocations.length > 0 && (
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Selected Locations</label>
                            <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-lg border border-light-gray">
                                {painLocations.map(location => (
                                <span key={location} className="bg-primary-light text-primary text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5">
                                    {location}
                                    <button
                                        onClick={() => handlePainLocationSelect(location)}
                                        className="text-primary/70 hover:text-red-500 font-bold"
                                        aria-label={`Remove ${location}`}
                                    >
                                    &#x2715;
                                    </button>
                                </span>
                                ))}
                            </div>
                        </div>
                    )}


                    <div>
                        <label htmlFor="pain-notes" className="block text-sm font-semibold text-gray-700 mb-1">Notes (Optional)</label>
                        <textarea
                            id="pain-notes"
                            value={painNotes}
                            onChange={(e) => setPainNotes(e.target.value)}
                            rows={3}
                            placeholder="e.g., Sharp, stabbing pain in the left shoulder when lifting arm."
                            className="w-full p-2 border rounded-md bg-white text-gray-800 placeholder-gray-500 focus:ring-primary-light focus:border-primary border-gray-300"
                        />
                    </div>

                    {renderButtons(id)}
                    </div>
                </div>
            )}
           {id === HealthCategory.PHYSIO && (
            <div className={EXPANDED_PANEL_CLASS}>
                 <div className={`space-y-6 p-2 rounded-lg transition-colors ${errors.physio ? 'bg-red-50 border border-red-300' : ''}`}>
                 {errors.physio && <p className="text-red-600 text-sm mb-2 font-semibold">{errors.physio}</p>}
                 <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Pain on Movement: <span className="font-bold text-primary">{physioPainOnMovement}</span>/10</label>
                    <input
                        type="range"
                        min="0"
                        max="10"
                        value={physioPainOnMovement}
                        onChange={e => setPhysioPainOnMovement(parseInt(e.target.value, 10))}
                        className="w-full h-2 bg-light-gray rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                 </div>
                 <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Session Activities</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {physiotherapyActivities.map(({ label, icon }) => (
                            <button
                                key={label}
                                onClick={() => handlePhysioActivitySelect(label)}
                                className={`p-2 rounded-lg text-center transition-all duration-200 border-2 flex flex-col items-center justify-center aspect-square ${
                                    physioActivities.includes(label) ? 'bg-primary border-primary text-white' : 'bg-white border-light-gray hover:border-primary-light text-gray-700'
                                }`}
                            >
                                <div className="w-8 h-8 flex items-center justify-center">{icon}</div>
                                <span className="text-xs font-semibold mt-1">{label}</span>
                            </button>
                        ))}
                    </div>
                 </div>
                 <div>
                    <label htmlFor="physio-notes" className="block text-sm font-semibold text-gray-700 mb-1">Notes / Observations</label>
                    <LexicalQuillCompat
                        theme='snow'
                        value={physioNotes}
                        onChange={(html) => setPhysioNotes(html)}
                        placeholder=""
                        onPasteHTML={(html, event) => {
                            event.preventDefault();
                            let text = '';
                            if (event.clipboardData) {
                                text = event.clipboardData.getData('text/plain');
                            } else if ((window as any).clipboardData) {
                                text = (window as any).clipboardData.getData('Text');
                            }
                            if (text) {
                                document.execCommand('insertText', false, text);
                            }
                        }}
                    />
                 </div>
                 <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Attachments</label>
                     <PhotoUploader 
                        previews={physioPhotos}
                        onUpload={(e: any) => handlePhotoUpload(e, true)}
                        onRemove={(idx: number) => removePhoto(idx, true)}
                        onOpenCamera={() => handleOpenCamera(true)}
                        onTriggerUpload={() => triggerFileUpload(true)}
                        fileInputRef={physioFileInputRef}
                    />
                 </div>
                 {renderButtons(id)}
                 </div>
            </div>
           )}
           {id === HealthCategory.NOTES && (
               <div className={EXPANDED_PANEL_CLASS}>
                    <ImportantToggle
                        value={noteImportant}
                        onChange={setNoteImportant}
                        id="note-entry-important"
                    />
                    {noteImportant && <ImportantWarningAlert />}
                    <textarea
                        value={notes}
                        onChange={handleNoteChange}
                        rows={6}
                        maxLength={NOTE_CHAR_LIMIT}
                        placeholder="Enter any notes here..."
                        className={`mt-3 w-full p-3 border rounded-md bg-white text-gray-800 placeholder-gray-500 focus:ring-primary-light focus:border-primary ${errors.notes || errors.notes_required ? 'border-red-500' : 'border-gray-300'}`}
                    />
                     {errors.notes_required && <p className="text-red-500 text-xs mt-1">{errors.notes_required}</p>}
                    <p className={`text-right text-sm ${errors.notes ? 'text-red-500' : 'text-gray-500'}`}>
                        {notes.length}/{NOTE_CHAR_LIMIT}
                    </p>
                    {renderButtons(id)}
               </div>
           )}
           {id === HealthCategory.IMPORTANT_NOTE && (
               <div className={EXPANDED_PANEL_CLASS}>
                    <ImportantToggle
                        value={importantNoteImportant}
                        onChange={setImportantNoteImportant}
                        id="important-note-entry-important"
                    />
                    <ImportantWarningAlert />
                    <textarea
                        value={importantNoteText}
                        onChange={(e) => setImportantNoteText(e.target.value)}
                        rows={6}
                        maxLength={NOTE_CHAR_LIMIT}
                        placeholder="Enter the important note here..."
                        className={`mt-3 w-full p-3 border rounded-md bg-white text-gray-800 placeholder-gray-500 focus:ring-primary-light focus:border-primary ${errors.important_note_required ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {errors.important_note_required && <p className="text-red-500 text-xs mt-1">{errors.important_note_required}</p>}
                    <p className={`text-right text-sm text-gray-500`}>
                        {importantNoteText.length}/{NOTE_CHAR_LIMIT}
                    </p>
                    {renderButtons(id)}
               </div>
           )}
           {id === HealthCategory.PHOTOS && (
               <div className={EXPANDED_PANEL_CLASS}>
                 <PhotoUploader 
                    previews={photoPreviews}
                    onUpload={(e: any) => handlePhotoUpload(e, false)}
                    onRemove={(idx: number) => removePhoto(idx, false)}
                    onOpenCamera={() => handleOpenCamera(false)}
                    onTriggerUpload={() => triggerFileUpload(false)}
                    fileInputRef={fileInputRef}
                    error={errors.photos}
                 />
                 {renderButtons(id)}
               </div>
           )}
          </AccordionCard>
            );
          })}
      </div>
      
      {isCameraOpen && (
        <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50">
            <video
                ref={videoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
            ></video>

            {/* Top Controls */}
            <div className="absolute top-5 left-5 right-5 flex justify-between items-center">
                <button
                    onClick={closeCamera}
                    className="bg-black/40 text-white rounded-full p-3 transition-opacity hover:opacity-80"
                    aria-label="Close camera"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
                <button
                    onClick={switchCamera}
                    className="bg-black/40 text-white rounded-full p-3 transition-opacity hover:opacity-80"
                    aria-label="Switch camera"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                       <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5m11 2a7 7 0 01-7 7H4m16-5v-5h-5m-6 2a7 7 0 007-7h5" />
                    </svg>
                </button>
            </div>
            
            {/* Bottom Controls */}
            <div className="absolute bottom-10 left-0 right-0 flex justify-center items-center">
                <button
                    onClick={handleCapture}
                    className="w-20 h-20 rounded-full bg-white/90 border-4 border-black/20 focus:outline-none focus:ring-4 focus:ring-offset-0 focus:ring-offset-black/0 focus:ring-white/50"
                    aria-label="Capture photo"
                ></button>
            </div>
        </div>
      )}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};
export default HealthInputPanel;
